import React, { useMemo } from 'react';

// Components
import { DrawdownAnalysisChart, GrowthChart, RiskRewardPlot } from '@/views/components/charts';

// Libraries
import Highcharts from 'highcharts/highstock';

// Types
import Comparison from '@/types/comparison';
import HistoricalScenario from '@/types/historical-scenario';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import User from '@/types/user';

// Utils
import { formatMessageWithLocale, FormattedMessage } from '@/utils/i18n';
import { getHistoricalScenariosChartOptions, getXAxisFormatter } from '../utils/chart-options';
import { getEarliestAndLatestDatesFromSeries } from '@/utils/disclaimers/date-helpers/date-helpers';
import { PERFORMANCE_CHART } from '@/utils/constants';
import { ROLLING_INTERVALS } from '@/utils/portfolios';
import { LoadingSpinner } from '@/views/components/common';
import moment from 'moment';
import calculateStartAndEndDates from '@/utils/historical-scenarios/start-and-end-date';

interface ScenarioChartProps {
  activeComparison: Comparison | undefined;
  drawdownData: any[];
  scatterPlotData: any[];
  seriesData: Highcharts.SeriesOptionsType[];
  locale: Locale;
  selectedHistoricalScenarios?: HistoricalScenario[];
  selectedChart: PERFORMANCE_CHART;
  user: Nullable<User> | undefined;
  isLoading: boolean;
}

const ScenarioGraph: React.FC<ScenarioChartProps> = ({
  activeComparison,
  drawdownData,
  scatterPlotData,
  seriesData,
  locale,
  selectedChart,
  selectedHistoricalScenarios,
  user,
  isLoading,
}) => {
  const { startDate, endDate } = getEarliestAndLatestDatesFromSeries(seriesData);
  const startDateStr = startDate ? moment.utc(new Date(startDate)).format(formatMessageWithLocale('Common.DateFormats.Short', locale)) : '';
  const endDateStr = endDate ? moment.utc(new Date(endDate)).format(formatMessageWithLocale('Common.DateFormats.Short', locale)) : '';
  const disclaimer = formatMessageWithLocale('Disclaimers.HistoricalScenariosDisclaimer', locale, { startDate: startDateStr, endDate: endDateStr });

  const HistoricalScenariosChartOptions = useMemo(
    () => getHistoricalScenariosChartOptions({ height: undefined, locale, animate: true }, selectedHistoricalScenarios, selectedChart, scatterPlotData),
    [locale, selectedHistoricalScenarios, selectedChart, scatterPlotData],
  );

  const { startDate: startDateOfMetrics, endDate: endDateOfMetrics } = selectedHistoricalScenarios
    ? calculateStartAndEndDates(selectedHistoricalScenarios)
    : { startDate: null, endDate: null };

  const fullChartOptions = useMemo(
    () => ({
      ...HistoricalScenariosChartOptions,
      ...(selectedChart === PERFORMANCE_CHART.DRAWDOWN && {
        xAxis: getXAxisFormatter(locale, HistoricalScenariosChartOptions.xAxis),
      }),
      series: seriesData,
    }),
    [HistoricalScenariosChartOptions, seriesData],
  );

  if (isLoading) {
    return (
      <div className="scenario-graph-container">
        <div className="scenario-graph-loading flex-fill is-light body-md">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="scenario-graph-container">
      {seriesData.length === 0 ? (
        <div className="scenario-graph-empty empty-chart flex-fill is-light body-md">
          <FormattedMessage id="Charts.EmptyPeriod" />
        </div>
      ) : (
        <>
          {selectedChart === PERFORMANCE_CHART.GROWTH && (
            <GrowthChart
              chartOptions={fullChartOptions}
              disclaimer={disclaimer}
              disClaimerClassName="disclaimer"
              locale={locale}
              seriesLegendProps={{ options: { showHistoricalProjection: true } }}
            />
          )}

          {selectedChart === PERFORMANCE_CHART.RISK_REWARD && (
            <RiskRewardPlot
              items={activeComparison?.items || []}
              data={scatterPlotData}
              chartOptions={fullChartOptions}
              startAndEndDisclaimerDates={{ startDate: startDateOfMetrics, endDate: endDateOfMetrics }}
            />
          )}

          {selectedChart === PERFORMANCE_CHART.DRAWDOWN && (
            <DrawdownAnalysisChart
              data={drawdownData}
              chartOptions={fullChartOptions}
              fillArea
              interval={ROLLING_INTERVALS.MONTHLY} // Drawdown is only available in monthly intervals
              items={activeComparison?.items}
              localCurrency={user?.appConfig.localCurrency}
              seriesLegendProps={{ options: { showHistoricalProjection: true } }}
              shortHistory={activeComparison!.shortHistory}
              showBaseCurrencies={activeComparison!.showBaseCurrencies}
              useMonthly={false}
              startAndEndDisclaimerDates={{ startDate, endDate }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ScenarioGraph;
